import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import profileService from '../../services/profileService';
import masterService from '../../services/masterService';
import MasterPageWrapper from './common/MasterPageWrapper';
import FormSection from './common/FormSection';
import MasterToolbar from './components/MasterToolbar';
import MasterTable from './components/MasterTable';
import StatusBadge from './components/StatusBadge';
import ActionMenu from './components/ActionMenu';
import ConfirmModal from './components/ConfirmModal';
import usePagination from '../../hooks/usePagination';
import './MastersHub.css';
import './ProfileMasterPage.css';

const INITIAL_PROFILE = {
  profileId: 0,
  profileCode: '',
  profileName: '',
  subCategoryId: '',
  description: '',
  displayOrder: 0,
  isActive: true
};

const noopToast = () => {};

export const ProfileMasterPage = ({ showToast = noopToast }) => {
  const toastRef = useRef(showToast);
  useEffect(() => {
    toastRef.current = showToast;
  }, [showToast]);

  const notify = useCallback((msg, type = 'info') => {
    if (toastRef.current) {
      toastRef.current(msg, type);
    }
  }, []);

  // View mode: 'list' | 'form'
  const [viewMode, setViewMode] = useState('list');
  const [isEditMode, setIsEditMode] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form State
  const [formData, setFormData] = useState(INITIAL_PROFILE);
  const [errors, setErrors] = useState({});

  // Lookups & All available tests
  const [departments, setDepartments] = useState([]);
  const [allTests, setAllTests] = useState([]);
  const [loadingTests, setLoadingTests] = useState(false);

  // Selected tests for this profile (ordered array of investigation objects)
  const [selectedTests, setSelectedTests] = useState([]);

  // Builder Filters
  const [availSearch, setAvailSearch] = useState('');
  const [availDeptFilter, setAvailDeptFilter] = useState('');

  // List View State
  const [profiles, setProfiles] = useState([]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Centralized Pagination
  const { paginatedItems: pagedProfiles, paginationProps: profilePaginationProps } = usePagination({
    items: profiles,
    resetDeps: [debouncedSearch, deptFilter, statusFilter]
  });

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Confirm Modal (Status change)
  const [confirmTarget, setConfirmTarget] = useState(null);

  // Load Departments
  const loadDepartments = useCallback(async () => {
    try {
      const res = await masterService.getDepartments({ isActive: true });
      if (res && res.success) {
        setDepartments(res.data || []);
      }
    } catch (err) {
      console.error('Error fetching departments:', err);
    }
  }, []);

  // Load All Active Investigations for the builder
  const loadAllTests = useCallback(async () => {
    setLoadingTests(true);
    try {
      const res = await masterService.getInvestigations({ isActive: true, pageSize: 1000 });
      if (res && res.success) {
        setAllTests(res.data?.items || res.data || []);
      }
    } catch (err) {
      console.error('Error fetching investigations:', err);
    } finally {
      setLoadingTests(false);
    }
  }, []);

  // Load Profiles List
  const loadProfiles = useCallback(async () => {
    setLoadingProfiles(true);
    try {
      const params = {};
      if (statusFilter !== 'all') {
        params.isActive = statusFilter === 'active';
      }
      if (debouncedSearch.trim()) {
        params.search = debouncedSearch.trim();
      }
      if (deptFilter) {
        params.subCategoryId = parseInt(deptFilter, 10);
      }

      const res = await profileService.getProfiles(params);
      if (res && res.success) {
        setProfiles(res.data || []);
      }
    } catch (err) {
      console.error('Error fetching profiles:', err);
      notify(err.response?.data?.message || 'Failed to fetch profiles', 'error');
    } finally {
      setLoadingProfiles(false);
    }
  }, [debouncedSearch, deptFilter, statusFilter, notify]);

  useEffect(() => {
    loadDepartments();
    loadAllTests();
  }, [loadDepartments, loadAllTests]);

  useEffect(() => {
    if (viewMode === 'list') {
      loadProfiles();
    }
  }, [viewMode, loadProfiles]);

  // Open Create Form
  const handleAddNew = () => {
    setFormData(INITIAL_PROFILE);
    setSelectedTests([]);
    setErrors({});
    setIsEditMode(false);
    setViewMode('form');
  };

  // Open Edit Form
  const handleEdit = async (profile) => {
    setIsEditMode(true);
    setFormData({
      profileId: profile.profileId,
      profileCode: profile.profileCode || '',
      profileName: profile.profileName || '',
      subCategoryId: profile.subCategoryId || '',
      description: profile.description || '',
      displayOrder: profile.displayOrder || 0,
      isActive: profile.isActive
    });
    setErrors({});

    // Fetch existing components
    try {
      const res = await profileService.getProfileComponents(profile.profileId);
      if (res && res.success) {
        const comps = (res.data || []).map(c => ({
          investigationId: c.investigationId,
          testCode: c.testCode,
          name: c.investigationName,
          departmentName: c.departmentName,
          subCategoryId: c.subCategoryId
        }));
        setSelectedTests(comps);
      }
    } catch (err) {
      console.error('Error fetching profile components:', err);
      showToast('Could not load profile components', 'error');
    }

    setViewMode('form');
  };

  // Status Toggle
  const handleToggleStatus = (profile) => {
    setConfirmTarget({
      profileId: profile.profileId,
      profileName: profile.profileName,
      nextStatus: !profile.isActive
    });
  };

  const confirmStatusChange = async () => {
    if (!confirmTarget) return;
    try {
      await profileService.updateProfileStatus(confirmTarget.profileId, confirmTarget.nextStatus);
      showToast(`Profile ${confirmTarget.nextStatus ? 'activated' : 'deactivated'} successfully.`, 'success');
      setConfirmTarget(null);
      loadProfiles();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to update status', 'error');
    }
  };

  // Available tests filtered
  const availableTests = useMemo(() => {
    const selectedIds = new Set(selectedTests.map(t => t.investigationId));
    return allTests.filter(t => {
      if (selectedIds.has(t.investigationId)) return false;
      if (availDeptFilter && String(t.subCategoryId) !== String(availDeptFilter)) return false;
      if (availSearch.trim()) {
        const q = availSearch.toLowerCase();
        const matchesName = t.name?.toLowerCase().includes(q);
        const matchesCode = t.testCode?.toLowerCase().includes(q);
        if (!matchesName && !matchesCode) return false;
      }
      return true;
    });
  }, [allTests, selectedTests, availDeptFilter, availSearch]);

  // Test manipulation
  const handleAddTest = (test) => {
    setSelectedTests(prev => [...prev, test]);
  };

  const handleRemoveTest = (invId) => {
    setSelectedTests(prev => prev.filter(t => t.investigationId !== invId));
  };

  const handleMoveUp = (index) => {
    if (index <= 0) return;
    setSelectedTests(prev => {
      const arr = [...prev];
      const temp = arr[index - 1];
      arr[index - 1] = arr[index];
      arr[index] = temp;
      return arr;
    });
  };

  const handleMoveDown = (index) => {
    setSelectedTests(prev => {
      if (index >= prev.length - 1) return prev;
      const arr = [...prev];
      const temp = arr[index + 1];
      arr[index + 1] = arr[index];
      arr[index] = temp;
      return arr;
    });
  };

  // Drag and Drop ordering
  const [draggedIndex, setDraggedIndex] = useState(null);

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
  };

  const handleDrop = (targetIndex) => {
    if (draggedIndex === null || draggedIndex === targetIndex) return;
    setSelectedTests(prev => {
      const arr = [...prev];
      const [dragged] = arr.splice(draggedIndex, 1);
      arr.splice(targetIndex, 0, dragged);
      return arr;
    });
    setDraggedIndex(null);
  };

  // Form Validation
  const validateForm = () => {
    const errs = {};
    if (!formData.profileCode.trim()) errs.profileCode = 'Profile code is required';
    if (!formData.profileName.trim()) errs.profileName = 'Profile name is required';
    if (selectedTests.length === 0) errs.tests = 'A profile must contain at least 1 test';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Submit Save
  const handleSave = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      showToast('Please fix required fields.', 'warning');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        profileCode: formData.profileCode.trim().toUpperCase(),
        profileName: formData.profileName.trim(),
        subCategoryId: formData.subCategoryId ? parseInt(formData.subCategoryId, 10) : null,
        description: formData.description?.trim() || null,
        displayOrder: parseInt(formData.displayOrder, 10) || 0,
        investigationIds: selectedTests.map(t => t.investigationId)
      };

      if (isEditMode) {
        await profileService.updateProfile(formData.profileId, {
          ...payload,
          isActive: formData.isActive
        });
        // Update components
        await profileService.setProfileComponents(formData.profileId, payload.investigationIds);
        showToast('Profile updated successfully.', 'success');
      } else {
        await profileService.createProfile(payload);
        showToast('Profile created successfully with constituent tests.', 'success');
      }

      setViewMode('list');
      loadProfiles();
    } catch (err) {
      console.error('Error saving profile:', err);
      showToast(err.response?.data?.message || 'Error saving profile', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Table Columns
  const columns = [
    {
      key: 'profileCode',
      header: 'Profile Code',
      render: (row) => <strong style={{ color: 'var(--primary-600)' }}>{row.profileCode}</strong>
    },
    {
      key: 'profileName',
      header: 'Profile / Panel Name',
      render: (row) => (
        <div>
          <div style={{ fontWeight: 600 }}>{row.profileName}</div>
          {row.description && <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{row.description}</div>}
        </div>
      )
    },
    {
      key: 'departmentName',
      header: 'Department',
      render: (row) => row.departmentName || 'General / Multi-Disciplinary'
    },
    {
      key: 'activeComponentCount',
      header: 'Tests Included',
      render: (row) => (
        <span className="pf-panel-count">
          {row.activeComponentCount || 0} Tests
        </span>
      )
    },
    {
      key: 'displayOrder',
      header: 'Order',
      render: (row) => row.displayOrder || 0
    },
    {
      key: 'isActive',
      header: 'Status',
      render: (row) => <StatusBadge isActive={row.isActive} />
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => (
        <ActionMenu
          actions={[
            {
              label: 'Edit Profile',
              onClick: () => handleEdit(row)
            },
            {
              label: row.isActive ? 'Deactivate' : 'Activate',
              onClick: () => handleToggleStatus(row),
              danger: row.isActive
            }
          ]}
        />
      )
    }
  ];

  return (
    <MasterPageWrapper
      masterTitle="Clinical Profile / Panel Master"
      masterName="Profile"
      viewMode={viewMode}
      onToggleView={(mode) => {
        if (mode === 'form') handleAddNew();
        else setViewMode('list');
      }}
    >
      {viewMode === 'list' ? (
        <>
          <MasterToolbar
            searchPlaceholder="Search by Profile Code or Name..."
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            filters={[
              {
                id: 'deptFilter',
                label: 'Department',
                value: deptFilter,
                onChange: setDeptFilter,
                options: [
                  { value: '', label: 'All Departments' },
                  ...departments.map(d => ({ value: String(d.subCategoryId), label: d.name }))
                ]
              },
              {
                id: 'statusFilter',
                label: 'Status',
                value: statusFilter,
                onChange: setStatusFilter,
                options: [
                  { value: 'all', label: 'All Statuses' },
                  { value: 'active', label: 'Active Only' },
                  { value: 'inactive', label: 'Inactive Only' }
                ]
              }
            ]}
          />

          <MasterTable
            columns={columns}
            data={pagedProfiles}
            loading={loadingProfiles}
            emptyTitle="No Profiles Found"
            emptyDescription="Create your first clinical profile grouping (e.g. Lipid Profile, Diabetes Panel)."
            pagination={profilePaginationProps}
          />
        </>
      ) : (
        <form onSubmit={handleSave} className="fade-in">
          {/* Section 1: Basic Information */}
          <FormSection
            title="Profile Details"
            description="Specify standard identification and clinical department for this profile."
          >
            <div className="form-row">
              <div className="form-group">
                <label className="form-label required">Profile Code</label>
                <input
                  type="text"
                  className={`form-input ${errors.profileCode ? 'is-invalid' : ''}`}
                  placeholder="e.g. PROF_LIPID, PROF_LFT"
                  value={formData.profileCode}
                  onChange={(e) => setFormData({ ...formData, profileCode: e.target.value.toUpperCase() })}
                  disabled={isEditMode}
                />
                {errors.profileCode && <span className="form-error">{errors.profileCode}</span>}
              </div>

              <div className="form-group" style={{ flex: 2 }}>
                <label className="form-label required">Profile Name</label>
                <input
                  type="text"
                  className={`form-input ${errors.profileName ? 'is-invalid' : ''}`}
                  placeholder="e.g. Comprehensive Lipid Profile"
                  value={formData.profileName}
                  onChange={(e) => setFormData({ ...formData, profileName: e.target.value })}
                />
                {errors.profileName && <span className="form-error">{errors.profileName}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">Primary Department</label>
                <select
                  className="form-input"
                  value={formData.subCategoryId}
                  onChange={(e) => setFormData({ ...formData, subCategoryId: e.target.value })}
                >
                  <option value="">-- Multi-Disciplinary / General --</option>
                  {departments.map(d => (
                    <option key={d.subCategoryId} value={d.subCategoryId}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group" style={{ flex: 3 }}>
                <label className="form-label">Description / Clinical Purpose</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Summary of tests and clinical diagnostic scope"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>

              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Display Priority</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.displayOrder}
                  onChange={(e) => setFormData({ ...formData, displayOrder: e.target.value })}
                />
              </div>
            </div>
          </FormSection>

          {/* Section 2: Constituent Tests Dual-List Builder */}
          <FormSection
            title="Profile Constituent Tests"
            description="Select individual investigations from the Test Master and arrange their reporting sequence."
          >
            {errors.tests && (
              <div style={{ color: '#ef4444', fontSize: '0.85rem', marginBottom: '0.75rem', fontWeight: 600 }}>
                ⚠️ {errors.tests}
              </div>
            )}

            <div className="pf-builder-grid">
              {/* Left Panel: Available Tests */}
              <div className="pf-panel">
                <div className="pf-panel-header">
                  <span className="pf-panel-title">Available Investigations</span>
                  <span className="pf-panel-count">{availableTests.length}</span>
                </div>

                <div className="pf-panel-search">
                  <input
                    type="text"
                    className="pf-search-input"
                    placeholder="Search test name or code..."
                    value={availSearch}
                    onChange={(e) => setAvailSearch(e.target.value)}
                  />
                  <select
                    className="pf-dept-select"
                    value={availDeptFilter}
                    onChange={(e) => setAvailDeptFilter(e.target.value)}
                  >
                    <option value="">All Depts</option>
                    {departments.map(d => (
                      <option key={d.subCategoryId} value={d.subCategoryId}>{d.name}</option>
                    ))}
                  </select>
                </div>

                <div className="pf-panel-body">
                  {loadingTests ? (
                    <div className="pf-empty-placeholder">Loading test catalog...</div>
                  ) : availableTests.length === 0 ? (
                    <div className="pf-empty-placeholder">No matching investigations available.</div>
                  ) : (
                    availableTests.map(test => (
                      <div key={test.investigationId} className="pf-test-item">
                        <div className="pf-test-info">
                          <span className="pf-test-name">{test.name}</span>
                          <span className="pf-test-meta">
                            <code>{test.testCode}</code> • {test.subCategoryName || 'General'}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="pf-btn-icon pf-btn-add"
                          onClick={() => handleAddTest(test)}
                        >
                          + Add
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Right Panel: Selected Tests with Sequence & Reorder */}
              <div className="pf-panel">
                <div className="pf-panel-header">
                  <span className="pf-panel-title">Included in Profile (Sequential)</span>
                  <span className="pf-panel-count">{selectedTests.length} Selected</span>
                </div>

                <div className="pf-panel-body">
                  {selectedTests.length === 0 ? (
                    <div className="pf-empty-placeholder">
                      No tests added yet.<br />
                      Click <strong>+ Add</strong> on tests from the left catalog.
                    </div>
                  ) : (
                    selectedTests.map((test, index) => (
                      <div
                        key={test.investigationId}
                        className="pf-test-item selected-row"
                        draggable
                        onDragStart={() => handleDragStart(index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDrop={() => handleDrop(index)}
                      >
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <span className="pf-item-seq">{index + 1}</span>
                          <div className="pf-test-info">
                            <span className="pf-test-name">{test.name}</span>
                            <span className="pf-test-meta">
                              <code>{test.testCode}</code> • {test.departmentName || test.subCategoryName || 'Test'}
                            </span>
                          </div>
                        </div>

                        <div className="pf-order-actions">
                          <button
                            type="button"
                            className="pf-btn-icon"
                            title="Move Up"
                            disabled={index === 0}
                            onClick={() => handleMoveUp(index)}
                          >
                            ▲
                          </button>
                          <button
                            type="button"
                            className="pf-btn-icon"
                            title="Move Down"
                            disabled={index === selectedTests.length - 1}
                            onClick={() => handleMoveDown(index)}
                          >
                            ▼
                          </button>
                          <button
                            type="button"
                            className="pf-btn-icon pf-btn-remove"
                            title="Remove"
                            onClick={() => handleRemoveTest(test.investigationId)}
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </FormSection>

          {/* Form Actions */}
          <div className="form-actions" style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setViewMode('list')}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving}
            >
              {saving ? 'Saving Profile...' : isEditMode ? 'Update Profile' : 'Save Profile'}
            </button>
          </div>
        </form>
      )}

      {/* Confirm Modal */}
      {confirmTarget && (
        <ConfirmModal
          isOpen={true}
          title={`${confirmTarget.nextStatus ? 'Activate' : 'Deactivate'} Profile`}
          message={`Are you sure you want to ${confirmTarget.nextStatus ? 'activate' : 'deactivate'} profile "${confirmTarget.profileName}"?`}
          confirmLabel={confirmTarget.nextStatus ? 'Activate' : 'Deactivate'}
          confirmVariant={confirmTarget.nextStatus ? 'primary' : 'danger'}
          onConfirm={confirmStatusChange}
          onCancel={() => setConfirmTarget(null)}
        />
      )}
    </MasterPageWrapper>
  );
};

export default ProfileMasterPage;
